import React from 'react';
import {
  Button, Select, TextInput, NumberInput, ErrorState, EmptyState, Badge, Alert,
  IconButton, FilterChip, ConfirmDialog, Checkbox, useToast,
} from '@parsina/framework';
import { ListChecks, Trash2, Lock, CheckCircle2, ExternalLink, Plus } from 'lucide-react';
import { C, CT } from '../../../constants/theme';
import { LTR_TEXT, autoDir, useTranslation } from '../../../i18n/I18nProvider.jsx';
import Panel from '../../../components/common/Panel.jsx';
import { formatMoney } from '../../../lib/money.js';
import { clinicPanelUrl } from '../../../lib/runtimeEnv.js';
import { useClinicScope } from '../../../hooks/useClinicScope.jsx';
import NewServiceDialog from './NewServiceDialog.jsx';
import { PlanPanelSkeleton } from '../Visit.skeleton.jsx';
import TreatmentRecordDialog from './TreatmentRecordDialog.jsx';
import {
  nextPlanItemStatuses, isTerminalPlanItem, isPlanLocked, labelFor,
  fieldsForService, linePrice, entryPrice, outstandingTotal, sessionsOf,
  canRecordItem, recordsForVisit, recordTitle, planCurrencyOf, clinicCurrencyFor,
  planErrorMessage,
} from '../Visit.shared.js';

// «پلن درمان» — the BOTTOM of the persistent column (§3.1.3).
//
//   "Integration: link treatments directly to specific teeth on the Odontogram"
//   "Workflow: supports adding planned procedures, surgeries, or follow-ups"
//   "Tracking: status tracking (Pending, Scheduled, Completed)"
//
// ── This is the DENTAL SERVICE's plan, not a second model of one ────────────
// طرح درمان already exists in this app's BFF: price-locked at approval,
// patient-signed, append-only. This section is a view onto it, and everything
// it does is one of the mutations that service already validates. A visit-local
// plan would be a second, unsigned quote that disagrees with the one the
// patient put their name to.
//
// ── No «تأیید و قفل مبلغ» ──────────────────────────────────────────────────
// The BFF still HAS `approveTreatmentPlan`, which locks a plan's amounts and
// freezes its document. This screen deliberately does not call it: the product
// owner's answer was that a dentist neither needs nor uses a signing step, and
// a button that is pressed by nobody is a button that teaches people to ignore
// buttons. `isPlanLocked` stays because a plan approved elsewhere must still
// render as read-only — what went is the way to CREATE that state from here.
//
// ── The status vocabulary ──────────────────────────────────────────────────
// §3.1.3 says "Pending, Scheduled, Completed". The service stores five states
// and they are the ones that get written; the mapping is documented in
// Visit.shared.js. Offering the document's three words verbatim would mean
// writing a status the service rejects.

const STATUS_TONE = {
  proposed: { bg: CT.warnSoft, ink: CT.warnInk },
  accepted: { bg: CT.accentSoft, ink: CT.accentInk },
  done: { bg: CT.goodSoft, ink: CT.goodInk },
  rejected: { bg: CT.surface2, ink: C.textSecondary },
  deferred: { bg: CT.surface2, ink: C.textSecondary },
};

const PHASES = ['emergency', 'phase1', 'phase2', 'maintenance'];

export default function VisitTreatmentPlan({
  t, formatNumber, plans, catalog, editable, selectedTooth, onSelectTooth,
  addRequestTooth, onAddRequestHandled,
  /**
   * A recorded treatment wrote to the chart. The odontogram is a SEPARATE read
   * owned by the screen, so it has to be told — nothing else here can reach it.
   */
  onChartChanged,
  // Where «تعریف خدمات در پنل کلینیک» goes when this clinic has declared none.
  clinicId,
  // THE VISIT a record is written against. Without it nothing here can record:
  // a treatment belongs to the appointment it was done in, and the service
  // requires one.
  appointmentId,
  // See VisitSubjective: the phone folds this section rather than wrapping it.
  collapsible = false, defaultOpen = true,
}) {
  const {
    plan, loading, error, saving, addItem, removeItem, setItemStatus,
    createService, catalogError, reload, records = [], recordTreatment, voidRecord,
  } = plans;

  const toast = useToast();
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState({
    catalogId: '', toothFdi: '', phase: 'phase1', description: '', quantity: 1, surfaces: '',
  });
  const [failure, setFailure] = React.useState(null);
  // «خدمت جدید» — open when the list does not have what the dentist wants.
  const [definingService, setDefiningService] = React.useState(false);
  // «ثبت انجام» — the plan line being recorded, or `{}` for «خارج از برنامه».
  // One piece of state rather than two: open-ness and which line are the same
  // question, and holding them apart is how a dialog opens on the last line.
  const [recording, setRecording] = React.useState(null);
  // «ابطال» takes a reason the service requires, so it asks for one.
  const [voiding, setVoiding] = React.useState(null);
  const [voidReason, setVoidReason] = React.useState('');
  // «وضعیت چارت هم باطل شود» — ticked, because a mistaken treatment usually
  // means a mistaken chart change. Not assumed, because it does not always: a
  // dentist voiding a BILLING duplicate has a tooth that really is crowned.
  const [voidChart, setVoidChart] = React.useState(true);
  // WHAT the last add put in the plan — its wording and tooth, never its id.
  // The service REPLACES items as a set and reissues ids on every write (see
  // addItem), so an id held across a save names a different line or none.
  const [justAdded, setJustAdded] = React.useState(null);

  // The odontogram's «افزودن به طرح درمان» opens this form with the tooth
  // already filled in — §3.1.3's "linked directly to specific teeth on the
  // Odontogram", driven from the chart rather than from a number field.
  //
  // AND BRINGS THE DENTIST TO IT. The chart, شرح حال and this section share one
  // scrolling column, so the form this opens is normally two sections below the
  // button that opened it — off-screen. Pressing «افزودن به طرح درمان» then
  // looked like it did nothing, and the tooth it prefilled was never seen.
  // Scrolling the form into view and focusing the one field the dentist must
  // still fill (the treatment — the button cannot know which) is what makes the
  // press legible as "this opened something".
  React.useEffect(() => {
    if (!addRequestTooth) return;
    setAdding(true);
    setDraft((d) => ({ ...d, toothFdi: addRequestTooth }));
    setJustAdded(null);
    onAddRequestHandled();
  }, [addRequestTooth, onAddRequestHandled]);

  const addFormRef = React.useRef(null);
  const requested = React.useRef(false);
  requested.current = requested.current || !!addRequestTooth;

  // After the form has actually rendered, not in the same tick that asks for it.
  React.useEffect(() => {
    if (!adding || !requested.current || !addFormRef.current) return;
    requested.current = false;
    // Optional: scrollIntoView is absent in jsdom, and calling it unguarded
    // throws INSIDE the effect — which takes the focus below down with it and
    // leaves the dentist on a form that is neither scrolled to nor focused.
    // The scroll is the nicety here; the focus is the part that must not fail.
    addFormRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    addFormRef.current.querySelector('select, input')?.focus();
  }, [adding]);

  const locked = isPlanLocked(plan);
  // A signed plan's LINES are frozen, but the dentist is not stuck: adding a
  // treatment starts a NEW course (useVisit's addItem). So «افزودن اقدام» stays
  // available on a locked plan — what it does changes, not whether it exists.
  // Removing a line and reordering remain refused, because those would edit the
  // signed document itself.
  const canEdit = editable && !locked;
  const canAdd = editable;

  // ── Money, in the currency it is counted in ────────────────────────────────
  // Every amount on the wire is minor units of a currency (whole rials for IRR),
  // and lib/money.js turns it into text: «۴٬۵۰۰٬۰۰۰ تومان» for IRR, exactly as
  // this screen always said it, «۱۲٫۵۰ AED» for a clinic whose books are in
  // dirhams. The PLAN's currency for its lines and total; an ENTRY's own for a
  // tariff in the picker. Never a conversion, and never two currencies summed.
  const { locale } = useTranslation();
  const money = (amount, currency) => formatMoney(amount, currency, { t, locale });
  const { clinics } = useClinicScope();
  const planCurrency = plan ? planCurrencyOf(plan) : null;
  // What the clinic keeps its books in NOW — what a new line, a new course or
  // a new service is quoted in. Null when this screen cannot tell (see
  // clinicCurrencyFor): no guess, because an IRR guess on an AED clinic reads
  // dirhams as toman.
  const clinicCurrency = clinicCurrencyFor({
    clinicId: plan?.clinicId || clinicId, plan, clinics, catalog,
  });
  // The currency a picked service must be priced in to join the plan. An open,
  // unsigned plan's own; otherwise the line starts a new course (see addItem),
  // which is quoted in the clinic's current currency.
  const quoteCurrency = plan && !isPlanLocked(plan) ? planCurrency : clinicCurrency;
  const clinicAppUrl = clinicPanelUrl(plan?.clinicId || clinicId);
  // A service whose dictionary row was RETIRED stays on the plans that already
  // quote it — a clinic mid-course cannot have its quotes stop working because
  // the dictionary was tidied — but it is not offered for a NEW line (D10).
  const offerable = catalog.filter((c) => !c.archived);
  // The service being added, and therefore which fields the form should ask
  // for. «کاربرد» is a HINT: a service that says nothing keeps the old
  // behaviour exactly — ask for a tooth, let it be left blank.
  const chosen = catalog.find((c) => c.id === draft.catalogId) || null;
  const asks = fieldsForService(chosen);
  const outstanding = outstandingTotal(plan, catalog);

  const submit = async () => {
    setFailure(null);
    if (!draft.catalogId) return;
    const entry = catalog.find((c) => c.id === draft.catalogId);
    // A service priced in another currency than the plan it would join is not
    // added: it could never be priced into it (approval refuses the whole plan),
    // and a line that silently reads «بدون تعرفه» would hide why.
    const fit = quoteCurrency ? entryPrice(entry, quoteCurrency) : null;
    if (fit?.mismatch) {
      // Both named by ISO code: the entry's may be IRR, and «تومان» must not
      // appear on a plan counted in anything else.
      setFailure({
        message: t('visit.plan.otherCurrencyRefused', {
          name: entry?.name || t('visit.plan.untitledItem'),
          currency: fit.currency,
          planCurrency: quoteCurrency,
        }),
      });
      return;
    }
    const err = await addItem({
      catalogId: draft.catalogId,
      // A whole-mouth service never carries a tooth, whatever is left in the
      // field from a previous pick: an invented tooth on a جرم‌گیری line ends
      // up on a document a patient signs.
      toothFdi: (asks.tooth && draft.toothFdi) || null,
      surfaces: (asks.surfaces && draft.surfaces) || null,
      phase: draft.phase,
      description: draft.description || entry?.name || null,
      // A three-session root canal is ONE line with quantity 3, not three
      // lines: the clinic's tariff is per session (D6).
      quantity: Math.max(1, Number(draft.quantity) || 1),
    });
    if (err) {
      setFailure(err);
      return;
    }
    // Say it landed, in BOTH registers: a toast for the dentist who is looking
    // somewhere else on the page (this form is often reached from the chart two
    // sections up), and a line that STAYS in the section for the one who is
    // looking right at it. A toast alone disappears; an inline line alone is
    // missed by whoever scrolled back to the odontogram.
    const what = draft.description || entry?.name || t('visit.plan.untitledItem');
    setJustAdded({ description: what, toothFdi: draft.toothFdi || null });
    toast.success(
      draft.toothFdi
        ? t('visit.plan.addedForTooth', { what, fdi: formatNumber(draft.toothFdi) })
        : t('visit.plan.added', { what }),
    );
    setDraft({ catalogId: '', toothFdi: '', phase: 'phase1', description: '', quantity: 1, surfaces: '' });
    setAdding(false);
  };

  const changeStatus = async (itemId, status) => {
    setFailure(null);
    const err = await setItemStatus(itemId, status);
    if (err) setFailure(err);
  };

  const drop = async (itemId) => {
    setFailure(null);
    const err = await removeItem(itemId);
    if (err) setFailure(err);
  };

  // What was done IN THIS VISIT — the audit view, voided rows included, because
  // a struck-out treatment is the row a dispute turns on.
  const visitRecords = recordsForVisit(records, appointmentId);
  // Recording needs a visit to write against. Without one the buttons are not
  // rendered at all rather than rendered and refused: an action that cannot
  // work is not an action.
  const canRecord = editable && !!appointmentId && !!recordTreatment;

  const record = async (input) => {
    setFailure(null);
    const result = await recordTreatment(input);
    if (!result?.error) {
      // A treatment may now write TOOTH EVENTS — «وضعیت دندان پس از درمان» —
      // and the chart above is a separate read that knows nothing about this
      // one. Without this, a dentist charts an extraction, sees the tooth still
      // standing, and charts it again by hand.
      //
      // Only when something was actually written: a جرم‌گیری changes no tooth,
      // and refetching the whole chart to learn that is a read for nothing.
      if (input.chartEvents?.length) onChartChanged?.();
      const what = input.serviceName
        || recording?.description
        || catalog.find((c) => c.id === input.catalogId)?.name
        || t('visit.plan.untitledItem');
      // Two facts, and the second is the one a dentist cannot see from here:
      // recording a treatment is what puts its price on this visit's invoice
      // (D17). Saying so once, at the moment it happens, is how «why did the
      // patient's bill change?» stops being a question for the desk.
      const billed = result?.record?.billingState === 'billed';
      toast.success(
        billed
          ? `${t('visit.record.saved', { what })} ${t('visit.record.billed')}`
          : t('visit.record.saved', { what }),
      );
    }
    return result;
  };

  const confirmVoid = async () => {
    const reason = voidReason.trim();
    if (!reason || !voiding) return;
    setFailure(null);
    // Only a treatment on a tooth can have changed one. Sending true for a
    // جرم‌گیری would be asking the service to undo something that never was.
    const alsoChart = !!voiding.toothFdi && voidChart;
    const { error: err, chargeReversed } = await voidRecord(voiding.id, reason, alsoChart);
    if (err) setFailure(err);
    // The charge comes back on its own now; the one case worth a word is the
    // one where it did not, because then the desk still has to act.
    if (!err && chargeReversed === false) {
      toast.warn(t('visit.record.voidCharge.notReversed'));
    }
    // The arch is a separate read and has just been made wrong by this write —
    // the same reason a recorded treatment refetches it.
    if (!err && alsoChart) onChartChanged?.();
    setVoiding(null);
    setVoidReason('');
    setVoidChart(true);
  };

  /**
   * Define a service and select it, in one gesture.
   *
   * Selecting it is the point: the dentist opened this because the picker did
   * not have what they were looking for, and leaving them to find it in the
   * list afterwards is that same problem a second time.
   */
  const defineService = async (input) => {
    const { entry, error: err } = await createService(input);
    if (err) return err;
    // A resolved call that answered nothing is not success. Without this the
    // next line throws inside the dialog's await, which leaves it open with no
    // message and an unhandled rejection in the console.
    if (!entry?.id) return new Error(t('visit.plan.newService.failed'));
    setDraft((d) => ({ ...d, catalogId: entry.id }));
    toast.success(t('visit.plan.newService.created', { name: entry.name }));
    return null;
  };


  // This panel alone, in its own shape — plan-line rows with their tooth
  // chip and status pill — inside the real Panel (DESIGN.md §"Loading &
  // skeleton", region load).
  if (loading) {
    return (
      <PlanPanelSkeleton
        t={t}
        label={t('common.loading')}
        collapsible={collapsible}
        defaultOpen={defaultOpen}
      />
    );
  }

  if (error) {
    return (
      <Panel title={t('visit.plan.title')}>
        <div role="alert">
          <ErrorState
            title={t('visit.plan.error.title')}
            description={planErrorMessage(t, error) || t('visit.plan.error.body')}
            onRetry={reload}
            retryLabel={t('common.actions.retry')}
          />
        </div>
      </Panel>
    );
  }

  const items = plan?.items || [];

  return (
    <Panel
      title={t('visit.plan.title')}
      collapsible={collapsible}
      defaultOpen={defaultOpen}
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {locked && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11.5, color: C.textSecondary,
            }}>
              <Lock size={13} aria-hidden />
              {t('visit.plan.locked')}
            </span>
          )}
          {/* What the OPEN lines still come to. On a signed plan it is the
              locked amounts; before that, the clinic's current tariffs — in
              the plan's currency, and only the lines priced in it. */}
          {outstanding.amount > 0 && (
            <span style={{ fontSize: 11.5, color: C.textSecondary }} data-testid="plan-outstanding">
              {t('visit.plan.outstanding', {
                amount: money(outstanding.amount, outstanding.currency),
              })}
            </span>
          )}
          {plan && <Badge variant="outline">{labelFor(t, 'visit.plan.planStatus', plan.status)}</Badge>}
          {canAdd && !adding && (
            <Button className="min-h-11" variant="outline" onClick={() => setAdding(true)}>
              {locked ? t('visit.plan.startNew') : t('visit.plan.add')}
            </Button>
          )}
        </div>
      }
    >
      <div data-testid="visit-plan" data-state={items.length ? 'content' : 'empty'}>
        {failure && (
          <Alert variant="error" style={{ marginBottom: 12 }}>
            {planErrorMessage(t, failure) || failure.message || t('visit.plan.saveFailed')}
          </Alert>
        )}

        {locked && (
          // Said out loud rather than left as missing buttons: the plan being
          // uneditable is a PROPERTY of a signed document, not a fault.
          <Alert variant="info" style={{ marginBottom: 12 }}>
            {t('visit.plan.lockedNote')} {t('visit.plan.lockedNextStep')}
          </Alert>
        )}

        {/* A clinic with no services and a clinic whose services could not be
            READ say two different things. The first sends the dentist to
            «کالا و خدمات» to define them; showing it during an outage would
            send them there to redefine twenty they already have. */}
        {catalogError ? (
          <Alert variant="warning" style={{ marginBottom: 12 }} data-testid="plan-catalog-error">
            {/* «no clinic» and «could not read» are both errors and neither is
                the other: one is retryable and the other never will be. */}
            {catalogError.kind === 'noClinic'
              ? t('visit.plan.noClinic')
              : t('visit.plan.catalogUnavailable')}
          </Alert>
        ) : catalog.length === 0 && (
          <Alert variant="warning" style={{ marginBottom: 12 }} data-testid="plan-no-services">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontWeight: 700 }}>{t('visit.plan.noServices.title')}</span>
              <span>{t('visit.plan.noServices.body')}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {clinicAppUrl && (
                  <a
                    href={clinicAppUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, minHeight: 44,
                      fontSize: 12.5, fontWeight: 700, color: CT.accentInk,
                    }}
                  >
                    {t('visit.plan.noServices.action')}
                    <ExternalLink size={13} aria-hidden />
                  </a>
                )}
                {/* And a way out WITHOUT leaving the visit. The «+» beside the
                    picker is behind «افزودن اقدام», which a dentist with no
                    services has no reason to press — so the one screen that
                    says «you have none» is exactly where the shortcut belongs. */}
                {canAdd && (
                  <Button
                    className="min-h-11"
                    variant="outline"
                    onClick={() => setDefiningService(true)}
                    data-testid="plan-new-service-empty"
                  >
                    <Plus size={14} aria-hidden style={{ marginInlineEnd: 4 }} />
                    {t('visit.plan.newService.title')}
                  </Button>
                )}
              </div>
            </div>
          </Alert>
        )}

        {/* «افزوده شد» — stays until the next add or the next request, so a
            dentist who scrolled away and came back still sees what happened.
            role=status so it is announced rather than only drawn. */}
        {justAdded && !adding && (
          <div
            role="status"
            data-testid="plan-just-added"
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: CT.goodSoft, border: `1px solid ${CT.good}`,
              borderRadius: 8, padding: '8px 10px', marginBottom: 12,
              fontSize: 12.5, fontWeight: 700, color: CT.goodInk,
            }}
          >
            <CheckCircle2 size={15} aria-hidden style={{ flexShrink: 0 }} />
            <span>
              {justAdded.toothFdi
                ? t('visit.plan.addedForTooth', {
                  what: justAdded.description,
                  fdi: formatNumber(justAdded.toothFdi),
                })
                : t('visit.plan.added', { what: justAdded.description })}
            </span>
          </div>
        )}

        {adding && canAdd && (
          <div
            ref={addFormRef}
            data-testid="plan-add-form"
            style={{
              border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 12,
              background: CT.surface2,
            }}
          >
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10,
            }}>
              {/* `aria-label` as well as `label` on every control here: the
                  kit's FormField renders its label as a <span> with no htmlFor
                  and no id, so the visible label is not attached to the
                  control — the same gap نسخه's RxRow documents. */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                label={t('visit.plan.service')}
                aria-label={t('visit.plan.service')}
                placeholder={t('visit.plan.choose')}
                value={draft.catalogId}
                onChange={(e) => setDraft((d) => ({ ...d, catalogId: e.target.value }))}
                options={offerable.map((c) => {
                  // The tariff is in the option itself. A dentist choosing
                  // between «کامپوزیت دو سطحی» and «سه سطحی» is choosing a
                  // price as much as a treatment, and making them add the line
                  // to find out is a quote written blind. «بدون تعرفه» is said
                  // out loud rather than shown as «۰ تومان», which would read
                  // as "this work is free".
                  //
                  // In the ENTRY's own currency — and a service priced in
                  // another currency than the plan it would join says so
                  // instead of showing a figure that cannot be added to it.
                  const price = entryPrice(c, quoteCurrency ?? undefined);
                  let tail;
                  if (price.mismatch) {
                    tail = t('visit.plan.otherCurrency', { currency: price.currency });
                  } else if (price.amount) {
                    tail = money(price.amount, price.currency);
                  } else {
                    tail = t('visit.plan.noTariff');
                  }
                  return { value: c.id, label: `${c.name} — ${tail}` };
                })}
              />
                </div>
                {/* The list is what the CLINIC performs, so a treatment missing
                    from it is a service the clinic has not defined — not a gap
                    in a curated vocabulary this app could fill. «+» defines it,
                    for the clinic and for every colleague (D3). */}
                <IconButton
                  variant="outline"
                  size={44}
                  icon={<Plus size={16} />}
                  label={t('visit.plan.newService.title')}
                  onClick={() => setDefiningService(true)}
                  data-testid="plan-new-service"
                />
              </div>

              {/* Asked for only where the service is performed on a tooth.
                  جرم‌گیری is not, and offering it a tooth field is how an
                  invented tooth lands on a signed document. A service that
                  says nothing still gets the field — that is what every
                  service did before «کاربرد» existed. */}
              {asks.tooth && (
                <div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <TextInput
                        label={t('visit.plan.tooth')}
                        dir="ltr"
                        value={draft.toothFdi}
                        aria-label={t('visit.plan.tooth')}
                        placeholder={t('visit.plan.wholeMouth')}
                        onChange={(e) => setDraft((d) => ({ ...d, toothFdi: e.target.value }))}
                      />
                    </div>
                    {selectedTooth && (
                      <Button
                        variant="subtle"
                        className="min-h-11"
                        onClick={() => setDraft((d) => ({ ...d, toothFdi: selectedTooth }))}
                      >
                        {formatNumber(selectedTooth)}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Surfaces, for a service that is named by them — a filling is
                  «یک/دو/سه سطحی». The same M D F L O I the tooth drawer uses. */}
              {asks.surfaces && (
                <TextInput
                  label={t('visit.plan.surfaces')}
                  dir="ltr"
                  value={draft.surfaces}
                  aria-label={t('visit.plan.surfaces')}
                  placeholder="MOD"
                  onChange={(e) => setDraft((d) => ({
                    ...d, surfaces: e.target.value.toUpperCase().replace(/[^MDFLOI]/g, ''),
                  }))}
                />
              )}

              {/* A course, not a repeat: the clinic's tariff is PER SESSION, so
                  a three-session root canal is ONE line with quantity 3 (D6). */}
              <NumberInput
                label={t('visit.plan.sessions')}
                aria-label={t('visit.plan.sessions')}
                min={1}
                value={draft.quantity}
                onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))}
              />

              <Select
                label={t('visit.plan.phase')}
                aria-label={t('visit.plan.phase')}
                placeholder=""
                value={draft.phase}
                onChange={(e) => setDraft((d) => ({ ...d, phase: e.target.value }))}
                options={PHASES.map((p) => ({ value: p, label: t(`visit.plan.phases.${p}`) }))}
              />
            </div>

            <div style={{ marginTop: 10 }}>
              <TextInput
                dir={autoDir(draft.description)}
                value={draft.description}
                aria-label={t('visit.plan.description')}
                placeholder={t('visit.plan.description')}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              />
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Button className="min-h-11" onClick={submit} disabled={saving || !draft.catalogId}>
                {saving
                  ? t('common.actions.saving')
                  : locked ? t('visit.plan.startNew') : t('visit.plan.addItem')}
              </Button>
              <Button className="min-h-11" variant="outline" onClick={() => setAdding(false)} disabled={saving}>
                {t('common.actions.cancel')}
              </Button>
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <EmptyState
            icon={<ListChecks size={40} color={C.textMuted} />}
            title={t('visit.plan.empty.title')}
            description={t('visit.plan.empty.description')}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((item) => {
              const tone = STATUS_TONE[item.status] || STATUS_TONE.proposed;
              // The locked amount once signed; the clinic's current tariff
              // before that. Null is «بدون تعرفه» — a service the clinic has
              // not priced, which is a state and not a gap — or a tariff in
              // another currency than the plan's, which is never priced in.
              const price = linePrice(item, catalog, { locked, currency: planCurrency });
              const sessions = sessionsOf(item, records);
              return (
                <div
                  key={item.id}
                  data-testid={`plan-item-${item.id}`}
                  style={{
                    display: 'flex', flexDirection: 'column', gap: 6,
                    border: `1px solid ${C.border}`, borderRadius: 10,
                    padding: '9px 11px', background: CT.surface,
                  }}
                >
                  {/* ── Line 1: WHAT, and what it costs ──────────────────────
                      The name has this line to itself. It used to share one
                      flex row with the tooth chip, the price, a status chip, a
                      132px dropdown, a record button and a delete — and in the
                      right-hand column of the workspace that is a few pixels
                      each, so the treatment's own name ellipsed away to
                      nothing. The name is the row; the controls are underneath
                      it. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* The tooth is a BUTTON: clicking it selects that tooth on
                        the odontogram above, which is the other direction of
                        §3.1.3's link — from the plan back to the anatomy. */}
                    {item.toothFdi ? (
                      <FilterChip
                        active={selectedTooth === item.toothFdi}
                        onClick={() => onSelectTooth(item.toothFdi)}
                        title={t('visit.plan.showOnChart')}
                        aria-label={t('visit.plan.showOnChart')}
                        className="flex-none"
                        label={<span {...LTR_TEXT}>{formatNumber(item.toothFdi)}</span>}
                      />
                    ) : (
                      <span style={{
                        fontSize: 11, color: C.textMuted, minWidth: 38,
                        textAlign: 'center', flexShrink: 0,
                      }}>
                        {t('visit.plan.wholeMouthShort')}
                      </span>
                    )}

                    <div
                      data-testid={`plan-item-name-${item.id}`}
                      style={{
                        flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700,
                        color: C.textPrimary,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {item.description || t('visit.plan.untitledItem')}
                    </div>

                    {/* «بدون تعرفه» is said in words: a «۰ تومان» here would
                        claim the work is free, and accounting declines to bill
                        it either way. A tariff in another currency is said in
                        words too — it is not this plan's to show. */}
                    <span
                      {...LTR_TEXT}
                      data-testid={`plan-item-amount-${item.id}`}
                      style={{
                        fontSize: 12, fontWeight: price.amount ? 700 : 400, flexShrink: 0,
                        color: price.amount ? C.textPrimary : CT.warnInk,
                      }}
                    >
                      {price.amount
                        ? money(price.amount, price.currency)
                        : price.mismatch
                          ? t('visit.plan.otherCurrency', { currency: price.entryCurrency })
                          : t('visit.plan.noTariff')}
                    </span>
                  </div>

                  {/* ── Line 2: where it is, and what may be done about it ──── */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      flex: 1, minWidth: 0, fontSize: 11, color: C.textMuted,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {labelFor(t, 'visit.plan.phases', item.phase)}
                      {item.surfaces ? ` · ${item.surfaces}` : ''}
                      {/* «جلسهٔ ۱ از ۳» — a course is one line with a session
                          count, not three lines (D6). Shown only where there
                          is more than one, so a single-session treatment is
                          not decorated with «۰ از ۱». */}
                      {sessions.multi ? ` · ${t('visit.plan.sessionsOf', {
                        done: formatNumber(sessions.done),
                        total: formatNumber(sessions.quoted),
                      })}` : ''}
                    </div>

                    {/* THE STATUS IS THE CONTROL, showing what it is set to.
                        It used to be a chip PLUS a separate «تغییر وضعیت…»
                        dropdown that always read empty — so picking a value
                        changed nothing anybody could see, and the field looked
                        broken. One control: its value is the line's status, and
                        its options are that plus wherever it may go next.

                        A FINISHED line has no control at all. «انجام‌شده» is
                        reached by recording the treatment that finished it, and
                        a done line is a record of work with a treatment row
                        pointing at it — it is not a state to be picked back
                        out of. Same for «رد شده»: the patient was asked and
                        said no, and undoing that from a menu loses the only
                        thing that mattered about it. */}
                    {editable && !isTerminalPlanItem(item.status) ? (
                      <div style={{ width: 138, flexShrink: 0 }}>
                        <Select
                          value={item.status}
                          placeholder={null}
                          aria-label={t('visit.plan.changeStatus', {
                            item: item.description || t('visit.plan.untitledItem'),
                          })}
                          data-testid={`plan-status-${item.id}`}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (next && next !== item.status) changeStatus(item.id, next);
                          }}
                          options={[item.status, ...nextPlanItemStatuses(item.status)]
                            .map((v) => ({ value: v, label: t(`visit.plan.status.${v}`) }))}
                        />
                      </div>
                    ) : (
                      <span
                        data-testid={`plan-status-${item.id}`}
                        style={{
                          background: tone.bg, color: tone.ink, borderRadius: 8,
                          padding: '3px 9px', fontSize: 11.5, fontWeight: 700, flexShrink: 0,
                        }}
                      >
                        {labelFor(t, 'visit.plan.status', item.status)}
                      </span>
                    )}

                    {/* «ثبت انجام» — THE button this whole feature is for. A
                        line becomes «انجام‌شده» by recording the work that
                        finished it, which is why `done` is not in the menu
                        beside this. On a multi-session line it says «ثبت جلسه»
                        instead, because one press is one session, not the
                        course. */}
                    {canRecord && canRecordItem(item, records) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-none min-h-11"
                        data-testid={`plan-record-${item.id}`}
                        onClick={() => setRecording(item)}
                      >
                        {sessions.multi ? t('visit.record.openSession') : t('visit.record.open')}
                      </Button>
                    )}

                    {/* A DONE line is not deletable. The work happened, a
                        treatment record points at this row, and removing it
                        would orphan the record that is the evidence for the
                        charge. «ابطال» on the record is how a mistake is
                        undone. */}
                    {canEdit && item.status !== 'done' && (
                      <IconButton
                        variant="ghost"
                        size={44}
                        icon={<Trash2 size={14} />}
                        label={t('visit.plan.remove', {
                          item: item.description || t('visit.plan.untitledItem'),
                        })}
                        onClick={() => drop(item.id)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── «انجام‌شده در این ویزیت» ────────────────────────────────────
            What was actually DONE today, which is the half of this screen the
            plan cannot answer. It sits below the quote rather than inside it
            because a record is not a plan line: some of these rows carry no
            line at all (work done outside the plan), and a course of three
            sessions is one line here and three rows below.

            «ابطال» voids, never deletes — the row stays, struck through, with
            its reason. That is the whole reason to have records instead of a
            status: a treatment that was taken back is a fact somebody may have
            to answer for. */}
        {visitRecords.length > 0 && (
          <div style={{ marginTop: 14 }} data-testid="visit-records">
            <div style={{
              fontSize: 12, fontWeight: 700, color: C.textSecondary, marginBottom: 8,
            }}>
              {t('visit.record.doneHere', { count: formatNumber(visitRecords.length) })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {visitRecords.map((r) => {
                const struck = !!r.voidedAtMs;
                return (
                  <div
                    key={r.id}
                    data-testid={`visit-record-${r.id}`}
                    data-state={struck ? 'voided' : 'live'}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      border: `1px solid ${C.border}`, borderRadius: 10,
                      padding: '8px 11px',
                      background: struck ? CT.surface2 : CT.goodSoft,
                      opacity: struck ? 0.75 : 1,
                    }}
                  >
                    <CheckCircle2
                      size={15}
                      color={struck ? C.textMuted : CT.goodInk}
                      aria-hidden
                      style={{ flexShrink: 0 }}
                    />
                    <span style={{
                      fontSize: 12, fontWeight: 700, color: C.textSecondary,
                      minWidth: 34, textAlign: 'center', flexShrink: 0,
                    }}>
                      {r.toothFdi
                        ? <span {...LTR_TEXT}>{formatNumber(r.toothFdi)}</span>
                        : t('visit.plan.wholeMouthShort')}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 12.5, fontWeight: 700, color: C.textPrimary,
                        textDecoration: struck ? 'line-through' : 'none',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {recordTitle(r, catalog) || t('visit.plan.untitledItem')}
                      </div>
                      <div style={{ fontSize: 11, color: C.textMuted }}>
                        {[
                          r.surfaces || null,
                          r.material ? t(`tooth.material.${r.material}`) : null,
                          r.outcome === 'in_progress'
                            ? t('visit.record.outcome.inProgress')
                            : null,
                          // The reason a struck row was struck. Without it the
                          // strike-through says «this did not happen», which is
                          // exactly the claim a void does NOT make.
                          struck ? t('visit.record.voidedFor', { reason: r.voidReason || '—' }) : null,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </div>

                    {/* «به صورت‌حساب اضافه نشد» — the treatment is recorded and
                        the charge is not. Said on the row rather than logged,
                        because the person who can do something about it is the
                        one looking at this screen, and a clinic only discovers
                        a missing charge when it counts the till.

                        Only `failed` earns a chip. «billed» is the normal case
                        and «none» means the service carries no declared tariff,
                        which the plan line already says in words. */}
                    {!struck && r.billingState === 'failed' && (
                      <span
                        data-testid={`record-unbilled-${r.id}`}
                        style={{
                          background: CT.warnSoft, color: CT.warnInk, borderRadius: 8,
                          padding: '3px 9px', fontSize: 11, fontWeight: 700, flexShrink: 0,
                        }}
                      >
                        {t('visit.record.notBilled')}
                      </span>
                    )}
                    {canRecord && !struck && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="flex-none min-h-11"
                        data-testid={`record-void-${r.id}`}
                        onClick={() => { setVoiding(r); setVoidReason(''); }}
                      >
                        {t('visit.record.void')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <TreatmentRecordDialog
          open={!!recording}
          onOpenChange={(next) => { if (!next) setRecording(null); }}
          onRecord={record}
          saving={saving}
          t={t}
          formatNumber={formatNumber}
          item={recording}
          planId={plan?.id || null}
          entry={catalog.find((c) => c.id === recording?.catalogId) || null}
          records={records}
        />

        {/* A void needs a REASON, and the service refuses one without it. Asked
            here rather than accepted as an empty string, because «why» is the
            only thing the voided row will still be able to say. */}
        <ConfirmDialog
          open={!!voiding}
          onOpenChange={(next) => {
            if (!next) { setVoiding(null); setVoidReason(''); setVoidChart(true); }
          }}
          title={t('visit.record.voidConfirm.title')}
          description={(
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.8, color: C.textSecondary }}>
                {t('visit.record.voidConfirm.body', {
                  what: recordTitle(voiding, catalog) || t('visit.plan.untitledItem'),
                })}
              </p>
              {/* WHAT HAPPENS TO THE BILL, said before the press.

                  Voiding strikes the clinical record out, and the charge it
                  raised is taken back through the order's session
                  (docs/TREATMENT_PROCESS_AND_SERVICE_BUNDLES_PLAN.md §5.4):
                  removed from an unissued invoice, zeroed on a paid draft,
                  credited on an issued one. If that fails the dentist is told
                  after the press too — a patient paying for work that was
                  struck out is still the harm this guards against. */}
              {voiding?.billingState === 'billed' && (
                <Alert variant="warning" data-testid="record-void-billing">
                  {t('visit.record.voidConfirm.stillBilled')}
                </Alert>
              )}

              {/* AND THE CHART. Striking out a crown while the tooth stays
                  crowned leaves the chart asserting what the record denies —
                  and until an event remembered which treatment caused it, this
                  could not even be offered.

                  Offered, not done: the dentist may be voiding a duplicate the
                  desk billed twice, and that tooth really is crowned. */}
              {!!voiding?.toothFdi && (
                <Checkbox
                  checked={voidChart}
                  onChange={(v) => setVoidChart(!!v)}
                  data-testid="record-void-chart"
                  label={t('visit.record.voidConfirm.alsoChart', {
                    tooth: formatNumber(voiding.toothFdi),
                  })}
                />
              )}
            </div>
          )}
          confirmLabel={t('visit.record.void')}
          cancelLabel={t('common.actions.cancel')}
          confirmDisabled={!voidReason.trim()}
          onConfirm={confirmVoid}
        >
          <TextInput
            aria-label={t('visit.record.voidReason')}
            placeholder={t('visit.record.voidReason')}
            dir={autoDir(voidReason)}
            value={voidReason}
            data-testid="record-void-reason"
            onChange={(e) => setVoidReason(e.target.value)}
          />
        </ConfirmDialog>

        <NewServiceDialog
          open={definingService}
          onOpenChange={setDefiningService}
          onCreate={defineService}
          saving={saving}
          t={t}
          clinicId={plan?.clinicId || clinicId}
          // What the typed tariff is parsed in: the clinic's books' currency,
          // which is what the BFF stores it as. Null (unknown) takes no tariff.
          currency={clinicCurrency}
          // What the dentist already typed as the line's شرح: they pressed
          // «خدمت جدید» because the list did not have it, so the name they were
          // reaching for is usually the one already in their hands.
          initialName={draft.description}
        />
      </div>
    </Panel>
  );
}
